import React, {useEffect, useState} from 'react';

import {Select} from '@/components';
import {
  getTransactionChannelsOptions,
  getTransactionTypesOptions,
} from '@/constants/formatters';
import {useProcessorOptions} from '@/hooks/useProcessorOptions';
import type {
  AfriexTransactionChannel,
  AfriexTransactionProcessor,
  CurrencySetting,
  ProcessorWithEmpty,
} from '@/types';
import {AfriexTransactionType, BUSINESS_RESTRICTED_PROCESSORS} from '@/types';

import BusinessConfigSummary from './BusinessConfigSummary';

interface BusinessTransactionProcessorConfigProps {
  business: CurrencySetting;
  onUpdateProcessor: (
    transactionType: AfriexTransactionType,
    channel: AfriexTransactionChannel,
    processor: AfriexTransactionProcessor,
  ) => void;
}

const BusinessTransactionProcessorConfig: React.FC<
  BusinessTransactionProcessorConfigProps
> = ({business, onUpdateProcessor}) => {
  const [selectedTransactionType, setSelectedTransactionType] = useState<
    AfriexTransactionType | ''
  >('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
    AfriexTransactionChannel | ''
  >('');
  const [selectedProcessor, setSelectedProcessor] = useState<
    AfriexTransactionProcessor | ''
  >('');

  // Sourced from the server so the list stays in sync with the backend
  // processor enum (falls back to the local enum while loading / on error).
  // This screen only ever writes the `business` subtree, so the retail-only
  // rails are excluded outright.
  const {processorOptions} = useProcessorOptions([
    ...BUSINESS_RESTRICTED_PROCESSORS,
  ]);

  // Get current processor for the selected combination
  const currentProcessor: ProcessorWithEmpty | undefined =
    selectedTransactionType && selectedPaymentMethod
      ? business[selectedTransactionType]?.[selectedPaymentMethod]
          ?.currentProcessor
      : undefined;

  // Check if processor is actually configured (not empty)
  const hasConfiguredProcessor = !!currentProcessor;

  // Update processor selector when payment method changes
  useEffect(() => {
    if (selectedTransactionType && selectedPaymentMethod) {
      const processor: ProcessorWithEmpty | undefined =
        business[selectedTransactionType]?.[selectedPaymentMethod]
          ?.currentProcessor;
      // Don't show empty string in the selector, treat it as empty
      setSelectedProcessor(processor || '');
    } else {
      setSelectedProcessor('');
    }
  }, [selectedTransactionType, selectedPaymentMethod, business]);

  const handleTransactionTypeChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    setSelectedTransactionType(e.target.value as AfriexTransactionType);
    // The only writer of `selectedTransactionType`, so resetting the payment
    // method here covers every case the old effect did.
    setSelectedPaymentMethod('');
  };

  const handlePaymentMethodChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    setSelectedPaymentMethod(e.target.value as AfriexTransactionChannel);
  };

  const handleProcessorChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const processor = e.target.value as AfriexTransactionProcessor;
    setSelectedProcessor(processor);

    if (selectedTransactionType && selectedPaymentMethod && processor) {
      onUpdateProcessor(
        selectedTransactionType,
        selectedPaymentMethod,
        processor,
      );
    }
  };

  const isProcessorSelectorEnabled =
    selectedTransactionType && selectedPaymentMethod;

  const txTypeOptions = getTransactionTypesOptions().filter(
    (option) =>
      option.value === AfriexTransactionType.DEPOSIT ||
      option.value === AfriexTransactionType.WITHDRAW,
  );

  return (
    <div className="space-y-3">
      <span className="text-xs text-gray-600 dark:text-gray-400">
        <strong>transaction type</strong> &raquo;{' '}
        <strong>payment method</strong> &raquo; <strong>processor</strong>
      </span>

      {/* Transaction Type Selector */}
      <Select
        className="w-full"
        options={txTypeOptions}
        value={selectedTransactionType}
        label="Transaction Type"
        placeholder="Select Transaction Type"
        onChange={handleTransactionTypeChange}
      />

      {/* Payment Method Selector */}
      <Select
        className="w-full"
        options={getTransactionChannelsOptions()}
        value={selectedPaymentMethod}
        label="Payment Method"
        placeholder={
          selectedTransactionType
            ? 'Select Payment Method'
            : 'Select Transaction Type First'
        }
        onChange={handlePaymentMethodChange}
        disabled={!selectedTransactionType}
      />

      {/* Processor Selector */}
      <div>
        <Select
          className="w-full"
          options={processorOptions}
          value={selectedProcessor}
          label="Current Processor"
          placeholder={
            isProcessorSelectorEnabled
              ? 'Select Processor'
              : 'Select Payment Method First'
          }
          onChange={handleProcessorChange}
          disabled={!isProcessorSelectorEnabled}
        />
      </div>

      {/* Configuration Summary */}
      {selectedTransactionType &&
        selectedPaymentMethod &&
        hasConfiguredProcessor && (
          <BusinessConfigSummary
            type={selectedTransactionType}
            channel={selectedPaymentMethod}
            processor={currentProcessor}
          />
        )}
    </div>
  );
};

export default BusinessTransactionProcessorConfig;
